/**
 * feat-241 — the station-readiness matrix (AC14, AC15, AC16).
 *
 * ── THE SCROLL CONTAINER: five load-bearing tokens (design §8) ───────────────
 * Track count is UNBOUNDED, so the first column is sticky and the horizontal
 * scroll is contained. THE PAGE BODY MUST NEVER SCROLL HORIZONTALLY.
 *
 *   1. `min-w-0` on the wrapper — a flex/grid child will not shrink below its
 *      content width, so `overflow-x-auto` silently never engages and the PAGE
 *      scrolls instead.
 *   2. `overflow-x-auto` on the scroller — and note it computes `overflow-y` to
 *      `auto` too, which is why a locked cell's full blocker list is a native
 *      `title` and not a popover.
 *   3. `w-max` on the table + per-column `min-w-[128px]` — without a floor the
 *      browser compresses the columns to fit and nothing scrolls.
 *   4. `relative` on the scroller. THIS ONE IS INVISIBLE IN REVIEW AND IT IS A
 *      COLLISION BETWEEN AC16 AND AC14. `position: absolute` descendants are NOT
 *      clipped by an ancestor's `overflow` unless that ancestor is itself
 *      positioned. Tailwind's `.sr-only` IS `position: absolute`, and AC16
 *      requires an `sr-only` "In progress" on every in-progress cell. With no
 *      positioned ancestor those spans escape to the initial containing block at
 *      their static position and extend the PAGE's scrollable width — measured
 *      at 1789px in a 1265px viewport before the fix. Nothing paints out there,
 *      no rect escapes an overflow ancestor, and "does the container have
 *      overflow-x" passes. Only the page's own scroll position catches it.
 *   5. `w-fit max-w-full` — caps the container at the available width when the
 *      matrix is wide (so it scrolls) and shrink-wraps it when a tenant has one
 *      track (so AC20's one-column matrix does not sit in 700px of empty white,
 *      which reads as broken). No conditional styling, no measuring in JS.
 *
 * ── THE STICKY COLUMN'S BACKGROUND ──────────────────────────────────────────
 * The sticky first cell's background must be OPAQUE and EQUAL TO ITS OWN ROW's.
 * A sticky cell with `bg-transparent`, or with one fixed fill while the rows
 * alternate, lets the scrolling cells slide visibly underneath it. That is the
 * single most common way a sticky-column table ships broken, and no geometry
 * assertion catches it — the geometry is correct while the pixels are wrong.
 *
 * The separator is a BOX-SHADOW, not a border: a `border-*` on a sticky cell does
 * not paint reliably under `border-collapse: collapse`.
 */
import React from 'react';
import { ChevronRight } from 'lucide-react';
import {
  CELL_NOT_ASSIGNED_FOOTNOTE,
  COL_CREW,
  COL_LOCATION,
  COL_TRACKS_COMPLETE,
  LESSON_COUNT,
  SORT_ASC,
  SORT_DESC,
  TRACKS_COMPLETE,
} from '../../../constants/curriculumCoverage';
import type {
  CoverageCrewMember,
  CoverageTrack,
} from '../../../services/curriculumCoverageService';
import { applicableTrackCount, cellApplies, notApplicableTrackIds } from './coverageApplies';
import { CoverageStateCell } from './CoverageStateCell';
import type { SortDirection, SortKey } from './coverageSort';

const STICKY_CELL =
  'sticky left-0 z-20 w-[264px] min-w-[264px] px-4 py-3 text-left align-middle shadow-[1px_0_0_0_rgba(0,0,0,0.08)]';
const STICKY_HEAD =
  'sticky left-0 z-30 w-[264px] min-w-[264px] bg-cream-dark px-4 py-3 align-bottom shadow-[1px_0_0_0_rgba(0,0,0,0.08)]';
const TRACK_HEAD =
  'w-[128px] min-w-[128px] border-l border-softBorder bg-cream-dark px-2 py-3 align-bottom';

/**
 * The sort affordance, visible AT REST on every column — an outline caret in
 * `text-inkMuted` at FULL OPACITY, 4.596:1 on the cream-dark band, clearing the
 * 3:1 floor WCAG 1.4.11 sets for a non-text graphic. The active column gets a
 * filled caret in `text-ink` (13.150:1), so active vs inactive is still a large
 * step. Deliberately not hover-only: a control nobody can see is a control
 * nobody uses, and this caret is the ONLY at-rest signal that a column sorts.
 *
 * ── `opacity-60` WAS HERE, AND THIS COMMENT USED TO LIE ABOUT IT ────────────
 * The class was `text-inkMuted opacity-60`, which composites to `#A2A0A5` =
 * **2.266:1** on `cream-dark` — below the 3:1 floor — while these very lines
 * claimed 4.596:1. The comment stated the ratio the code did not achieve, which
 * on this project is worse than no comment: a false comment is what stops the
 * next person measuring. Design's ship review filed it as the SOFT FAIL.
 *
 * THE GENERAL TRAP, worth naming because it defeats token-level testing: an
 * alpha modifier — `opacity-60` on the element, or `text-inkMuted/60` on the
 * colour — produces a colour THE TOKEN SYSTEM CANNOT SEE. `inkMuted` still
 * resolves to `#6A6B7A` and still measures 4.596:1, so a contrast test that
 * reads only the token passes while the render fails. `feat241CoverageContrast`
 * therefore composites the alpha itself rather than trusting the token, and
 * `state-cells.md` §7 records the same defect in `TrainingProgressPage`'s
 * `text-inkMuted/60` headers (2.415:1 on white) — pre-existing and out of scope,
 * but the identical mistake.
 */
const SortCaret: React.FC<{ active: boolean; direction: SortDirection }> = ({
  active,
  direction,
}) => (
  <svg
    aria-hidden
    width="9"
    height="9"
    viewBox="0 0 10 10"
    fill="currentColor"
    className={`mt-0.5 shrink-0 ${active ? 'text-ink' : 'text-inkMuted'}`}
  >
    {/* AN INACTIVE CARET MUST NOT POINT A DIRECTION (design F3).
        `direction` is the ONE global sort direction, so passing it to all 16+
        carets meant toggling a single column flipped every caret on the band —
        which reads as "all columns are now sorted descending". Only the active
        column has a direction; the rest get a neutral double-chevron meaning
        "this sorts", not "this is sorted".

        This compounds with the opacity fix rather than being independent of it:
        now that the carets are actually legible, a band of 15 wrong-pointing
        arrows is MORE noticeable, not less. */}
    {active ? (
      direction === 'asc' ? (
        <path d="M5 0 9 5H1z" />
      ) : (
        <path d="M5 10 1 5h8z" />
      )
    ) : (
      <path d="M5 0 8.5 3.5h-7zM5 10 1.5 6.5h7z" />
    )}
  </svg>
);

export interface CoverageMatrixProps {
  tracks: CoverageTrack[];
  /** Already sorted by the page — the matrix renders, it does not order. */
  crew: CoverageCrewMember[];
  sortKey: SortKey;
  sortDirection: SortDirection;
  onSort: (key: SortKey) => void;
  onOpenPassport: (serverId: string) => void;
}

function isActive(key: SortKey, active: SortKey): boolean {
  if (key.kind !== active.kind) return false;
  if (key.kind === 'track' && active.kind === 'track') return key.trackId === active.trackId;
  return true;
}

export const CoverageMatrix: React.FC<CoverageMatrixProps> = ({
  tracks,
  crew,
  sortKey,
  sortDirection,
  onSort,
  onOpenPassport,
}) => {
  const srSorted = sortDirection === 'asc' ? SORT_ASC : SORT_DESC;

  return (
    <div className="min-w-0">
      <div
        className="relative w-fit max-w-full overflow-x-auto rounded-2xl border border-softBorder bg-white"
        data-testid="coverage-scroller"
      >
        <table className="w-max border-separate border-spacing-0 text-left">
          <thead>
            <tr>
              {/* THREE sort controls in one cell, not one (QA F1 / P9).
                  The sticky area merges crew name, location and tracks-complete
                  into a single cell by design — P10 chose a flat roster with a
                  location column over group headers, because a third sticky
                  behaviour would fight the sticky first column and the horizontal
                  scroll. That layout decision is not a reason to drop two of the
                  four sort kinds: P9 records Preston choosing "every column is
                  sortable" OVER the narrower "summary column only". So the
                  sub-line the mockup shows under the crew header
                  ("Location · tracks complete") is rendered as two more sort
                  buttons rather than as static text. */}
              <th scope="col" className={STICKY_HEAD}>
                <button
                  type="button"
                  onClick={() => onSort({ kind: 'name' })}
                  data-sort-kind="name"
                  className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-ink"
                >
                  {COL_CREW}
                  <SortCaret active={isActive({ kind: 'name' }, sortKey)} direction={sortDirection} />
                  {isActive({ kind: 'name' }, sortKey) ? (
                    <span className="sr-only">— {srSorted}</span>
                  ) : null}
                </button>
                <div className="mt-0.5 flex items-center gap-1 text-[10px] font-normal normal-case tracking-normal text-inkMuted">
                  <button
                    type="button"
                    onClick={() => onSort({ kind: 'location' })}
                    data-sort-kind="location"
                    className="flex items-center gap-1 hover:text-ink"
                  >
                    {COL_LOCATION}
                    <SortCaret
                      active={isActive({ kind: 'location' }, sortKey)}
                      direction={sortDirection}
                    />
                    {isActive({ kind: 'location' }, sortKey) ? (
                      <span className="sr-only">— {srSorted}</span>
                    ) : null}
                  </button>
                  <span aria-hidden>·</span>
                  <button
                    type="button"
                    onClick={() => onSort({ kind: 'tracks_complete' })}
                    data-sort-kind="tracks_complete"
                    className="flex items-center gap-1 hover:text-ink"
                  >
                    {COL_TRACKS_COMPLETE}
                    <SortCaret
                      active={isActive({ kind: 'tracks_complete' }, sortKey)}
                      direction={sortDirection}
                    />
                    {isActive({ kind: 'tracks_complete' }, sortKey) ? (
                      <span className="sr-only">— {srSorted}</span>
                    ) : null}
                  </button>
                </div>
              </th>
              {tracks.map((track) => {
                const key: SortKey = { kind: 'track', trackId: track.id };
                const active = isActive(key, sortKey);
                return (
                  <th scope="col" key={track.id} className={TRACK_HEAD}>
                    <button
                      type="button"
                      onClick={() => onSort(key)}
                      data-sort-kind="track"
                      className="flex w-full items-start justify-between gap-1 text-left"
                    >
                      {/* Full opacity, NOT `text-inkMuted/60`: an alpha-modified
                          ink is not the token, it is a new colour that depends on
                          what is behind it (2.4:1 composited). */}
                      <span className="text-[11px] font-bold leading-tight text-ink">{track.title}</span>
                      <SortCaret active={active} direction={sortDirection} />
                      {active ? <span className="sr-only">— {srSorted}</span> : null}
                    </button>
                    <div className="mt-1 text-[10px] text-inkMuted">
                      {LESSON_COUNT(track.lessons_total)}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {crew.map((member, i) => {
              // The sticky cell and its row share ONE opaque background.
              const bg = i % 2 ? 'bg-cream' : 'bg-white';
              return (
                <tr key={member.server_id} className="group">
                  <th scope="row" className={`${STICKY_CELL} ${bg}`}>
                    <button
                      type="button"
                      onClick={() => onOpenPassport(member.server_id)}
                      className="flex w-full items-center justify-between gap-2 text-left group-hover:underline"
                    >
                      <span className="min-w-0">
                        {/* SECURITY HIGH-1 — masked from Sentry Session Replay.
                            `instrument.ts` runs replayIntegration with
                            `maskAllText: false` at a 0.1 session / 1.0 on-error
                            sample rate, so this roster would otherwise reach a
                            third-party processor. The field set is not new —
                            TrainingProgressPage renders the same fields — but the
                            POPULATION is: that roster requires
                            `bespoke_go_live_date IS NOT NULL` and inner-joins
                            `location`, and this feature deliberately drops both
                            (P2, P13), so a PRE-LAUNCH tenant's crew (Grove — the
                            tenant this feature exists for) reaches Sentry for the
                            first time.

                            THE `title` ATTRIBUTES ARE GONE ON PURPOSE, and this is
                            the half that is easy to miss. Verified in the installed
                            @sentry-internal/replay: `maskAttribute()` opens with
                            `if (!maskAllText) return value;`, so its
                            `maskAttributes: ['title','placeholder']` default NEVER
                            RUNS in this app. `data-sentry-mask` masks TEXT NODES
                            only (`needMaskingText`), so keeping `title={member.name}`
                            would have masked the visible name and shipped the
                            identical string in the attribute — a mask that masks
                            nothing. The tooltip on a truncated name is the cost.
                            Track titles keep their `title`: not personal data.

                            Masked per-span, NOT on the wrapper, because masking is
                            ancestor-based and the tracks-complete count below must
                            stay legible — it is not personal data and masking it
                            destroys the replay's value for debugging this surface. */}
                        <span
                          data-sentry-mask
                          className="block truncate text-sm font-bold text-ink"
                        >
                          {member.name}
                        </span>
                        {/* Fixed height so a blank location name (P13 — a dangling
                            FK still shows the crew member) does not shorten the row. */}
                        <span
                          data-sentry-mask
                          className="block h-4 truncate text-xs text-inkMuted"
                        >
                          {member.location_name}
                        </span>
                        <span className="mt-0.5 block text-[11px] font-bold text-ink">
                          {TRACKS_COMPLETE(member.tracks_complete, applicableTrackCount(member))}
                        </span>
                      </span>
                      <ChevronRight aria-hidden size={14} className="shrink-0 text-inkMuted" />
                    </button>
                  </th>
                  {tracks.map((track, j) => {
                    // `cells` is dense and track-ordered by contract, so index j
                    // IS this track's cell. No sparse-map reconciliation here,
                    // deliberately: a mismatch is a backend defect to report, and
                    // defensive lookup logic would hide it.
                    const cell = member.cells[j];
                    return (
                      <td
                        key={track.id}
                        className={`border-l border-t border-softBorder px-2 py-2.5 align-middle ${bg}`}
                      >
                        {cell ? (
                          <CoverageStateCell
                            cell={cell}
                            track={track}
                            tracks={tracks}
                            crewName={member.name}
                            notApplicable={notApplicableTrackIds(member)}
                          />
                        ) : null}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* feat-302 D2: the "—" cell has no other legend on the page. */}
      {crew.some((m) => m.cells.some((c) => !cellApplies(c))) ? (
        <p className="mt-2 px-1 text-xs text-inkMuted" data-testid="coverage-not-assigned-footnote">
          {CELL_NOT_ASSIGNED_FOOTNOTE}
        </p>
      ) : null}
    </div>
  );
};

export default CoverageMatrix;
